import { computed, reactive } from 'vue'
import {
  errorMessage,
  failureKind,
  requireData,
  requireField,
  rpc,
  rpcGet,
  verified,
  __setRefreshTermsStatusHook,
} from './api'
import type {
  BootData,
  ChangePasswordResponse,
  LoginResponse,
  OtpTiming,
  SignupPayload,
  SignupResponse,
  VerifyOtpResponse,
} from './types/api'
import { clearClientCache } from './composables/cacheLifecycle'

// Re-export for backward compatibility (types previously lived here).
export type {
  BootData,
  LoginResponse,
  SignupPayload,
  SignupResponse,
  VerifyOtpResponse,
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionState {
  boot: BootData
  loaded: boolean
  loading: boolean
  error: unknown
  /**
   * Set when the first session check got no usable answer (offline, timeout,
   * 5xx), which says nothing about whether the user is signed in. The app shows
   * "Can't reach the server" with a Retry instead of the Login page. The text
   * is for display only.
   */
  unreachable: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyBoot(): BootData {
  return {
    authenticated: false,
    user: null,
    full_name: null,
    user_image: null,
    roles: [],
    permissions: [],
    company: null,
    companies: [],
    entitlements: {},
    home_route: '/login',
    is_staff: false,
    is_member: false,
    csrf_token: null,
    can_access_desk: false,
    site_name: null,
    client_base_url: null,
    api_base_url: null,
    desk_url: '/app',
    is_impersonating: false,
    impersonated_by: null,
    original_user: null,
  }
}

// ---------------------------------------------------------------------------
// Reactive session state
// ---------------------------------------------------------------------------

export const session = reactive<SessionState>({
  boot: emptyBoot(),
  loaded: false,
  loading: false,
  error: null,
  unreachable: null,
})

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

export const tokenStorage = {
  get accessToken(): string | null {
    return sessionStorage.getItem('glamora_access_token')
  },
  set accessToken(val: string | null) {
    if (val) {
      sessionStorage.setItem('glamora_access_token', val)
    } else {
      sessionStorage.removeItem('glamora_access_token')
    }
  },
  get refreshToken(): string | null {
    return localStorage.getItem('glamora_refresh_token')
  },
  set refreshToken(val: string | null) {
    if (val) {
      localStorage.setItem('glamora_refresh_token', val)
    } else {
      localStorage.removeItem('glamora_refresh_token')
    }
  },
  clear() {
    this.accessToken = null
    this.refreshToken = null
  },
}

// ---------------------------------------------------------------------------
// Computed helpers
// ---------------------------------------------------------------------------

export const isLoggedIn = computed(() => !!session.boot?.authenticated)
export const roles = computed<string[]>(() => session.boot?.roles ?? [])

export function hasAnyRole(required: string[]): boolean {
  if (!required?.length) return true
  const userRoles = session.boot?.roles ?? []
  return required.some((r) => userRoles.includes(r))
}

export function isImpersonating(): boolean {
  return !!session.boot?.is_impersonating
}

// ---------------------------------------------------------------------------
// Role → home route
// ---------------------------------------------------------------------------

export function roleHomeRoute(boot: BootData): string {
  const r = boot.roles ?? []
  if (r.includes('Salon Owner') || r.includes('Salon Manager'))
    return '/dashboard'
  if (r.includes('Salon Receptionist')) return '/front-desk'
  if (r.includes('Salon Staff')) return '/my/day'
  if (r.includes('Customer')) return '/portal'
  if (r.includes('Glamora Platform Admin')) return '/platform/reports'
  return '/'
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((value) => set.has(value))
}

function sameFeatures(
  a: Record<string, boolean> | undefined,
  b: Record<string, boolean> | undefined,
): boolean {
  const left = a ?? {}
  const right = b ?? {}
  const keys = Object.keys(left)
  if (keys.length !== Object.keys(right).length) return false
  return keys.every((key) => left[key] === right[key])
}

/**
 * Did this boot change the scope the cache was built under?
 *
 * The `user` is the identity; `roles` and `features` are the permission
 * ceiling. A change in any of them invalidates every cached answer, so the
 * cache is cleared before the new boot is visible (`CACHE_AUDIT.md` §5.4).
 * Hard-coding `roles` is not enough — an owner can switch a feature off for a
 * role without the role list changing.
 */
function scopeChanged(previous: BootData, next: BootData): boolean {
  if ((previous.user ?? null) !== (next.user ?? null)) return true
  return (
    !sameSet(previous.roles ?? [], next.roles ?? []) ||
    !sameFeatures(previous.features, next.features)
  )
}

function applyBoot(data: unknown): void {
  let payload = data as Record<string, unknown>

  // Unwrap double-wrapped responses from frappeFetchPatch
  if (payload?.boot && payload.authenticated === undefined) {
    payload = payload.boot as Record<string, unknown>
  }

  const next = { ...emptyBoot(), ...(payload ?? {}) } as BootData

  // A different user, or the same user with different roles/features, means
  // every cached answer was scoped to something else. Drop the cache *before*
  // the new boot is visible, so nothing paints the previous scope's rows
  // (`CACHE_AUDIT.md` §5.4, `PLAN_TRACK_C.md` C2.5). A repeated identical boot
  // (the router guard, a terms refresh) must not thrash the cache.
  if (scopeChanged(session.boot, next)) clearClientCache()

  session.boot = next

  if (session.boot.csrf_token) {
    window.csrf_token = session.boot.csrf_token
  }
  if (session.boot.site_name) {
    window.site_name = session.boot.site_name
  }
  session.loaded = true
  session.loading = false
  session.error = null
  // An answer from the server proves it is reachable.
  session.unreachable = null

  // Install the global terms_not_accepted hook so api.ts can refresh the boot
  // when a 417 terms_not_accepted error is encountered.
  __setRefreshTermsStatusHook(async () => {
    try {
      await fetchBoot()
    } catch {
      /* ignore — the original error is what callers see */
    }
  })
}

/** How long the session check may take before the server counts as unreachable. */
const BOOT_TIMEOUT_MS = 20000

/** A failure to display when the server could not be reached. */
export function unreachableText(e: unknown): string {
  if (
    e instanceof TypeError ||
    (e as { name?: string })?.name === 'AbortError'
  ) {
    return 'The server did not answer. Check your internet connection.'
  }
  return errorMessage(e)
}

/**
 * Load the session. A failure is classified (`failureKind`):
 * - auth (401 / 403): the session is over; the boot becomes the signed-out one.
 * - anything else (network error, timeout, 5xx, an unreadable or empty answer):
 *   nothing is known about the session, so a boot already loaded is kept and
 *   the Error is rethrown for the caller to treat as "server unreachable".
 */
export async function fetchBoot(company?: string): Promise<BootData> {
  session.loading = true
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), BOOT_TIMEOUT_MS)
  try {
    const data = await rpcGet(
      'glamora.utils.session_api.get_frontend_boot',
      company ? { company } : {},
      { signal: controller.signal },
    )
    // An empty 2xx is not a boot: never turn it into a signed-out session.
    applyBoot(requireData(data, 'the session'))
    return session.boot
  } catch (e) {
    session.error = e
    if (failureKind(e) === 'auth') session.boot = emptyBoot()
    session.loaded = true
    session.loading = false
    throw e
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Check the session again after "Can't reach the server". Clears
 * `session.unreachable` when the server answered (signed in, or signed out
 * because it said so) and refreshes it when it still did not.
 */
export async function retryBoot(): Promise<void> {
  try {
    await fetchBoot()
    session.unreachable = null
  } catch (e) {
    session.unreachable = failureKind(e) === 'auth' ? null : unreachableText(e)
  }
}

// ---------------------------------------------------------------------------
// Auth — login
// ---------------------------------------------------------------------------

export type LoginResult =
  | { status: 'ok'; boot: BootData }
  | { status: 'otp_required'; user: string }
  | { status: 'password_change_required'; boot: BootData }

export async function login(usr: string, pwd: string): Promise<LoginResult> {
  try {
    // A 2xx without tokens is not a sign-in: never open the app on it.
    const data = requireField(
      (await rpc('glamora.api.auth.login', {
        usr,
        pwd,
      })) as LoginResponse | null,
      'access_token',
      'the sign-in',
    )

    tokenStorage.accessToken = data.access_token
    tokenStorage.refreshToken = data.refresh_token
    applyBoot(data.boot ?? data)

    if (data.required_password_change) {
      return { status: 'password_change_required', boot: session.boot }
    }

    return { status: 'ok', boot: session.boot }
  } catch (e: unknown) {
    // Backend throws 401 "Email not verified, OTP required" when email_verified=0.
    // Intercept here so Login.vue can show the OTP stage instead of an error banner.
    const err = e as { status?: number; message?: string }
    if (err?.status === 401 && /email not verified/i.test(err?.message ?? '')) {
      return { status: 'otp_required', user: usr }
    }
    throw e
  }
}

// ---------------------------------------------------------------------------
// Auth — logout
// ---------------------------------------------------------------------------

export async function logout(): Promise<void> {
  try {
    await rpc('glamora.api.auth.logout', {})
  } finally {
    clearClientCache()
    tokenStorage.clear()
    session.boot = emptyBoot()
    session.loaded = true
    session.loading = false
    window.csrf_token = undefined
  }
}

export async function logoutAll(): Promise<void> {
  try {
    await rpc('glamora.api.auth.logout_all', {})
  } finally {
    clearClientCache()
    tokenStorage.clear()
    session.boot = emptyBoot()
    session.loaded = true
    session.loading = false
    window.csrf_token = undefined
  }
}

// ---------------------------------------------------------------------------
// Auth — switch company (used by AppShell)
// ---------------------------------------------------------------------------

export async function switchCompany(company: string): Promise<BootData> {
  // fetchBoot wipes the boot on failure; a refused switch must leave the
  // user in the company they were already working in.
  const previous = session.boot
  // A company switch changes tenant scope, and the caller reloads the page
  // afterwards. The reload only flushes memory, so drop the persistent cache
  // and the room joins here (`CACHE_AUDIT.md` §5.4, `PLAN_TRACK_C.md` C2.5).
  clearClientCache()
  try {
    return await fetchBoot(company)
  } catch (e) {
    session.boot = previous
    session.error = null
    throw e
  }
}

// ---------------------------------------------------------------------------
// Auth — impersonation (support)
// ---------------------------------------------------------------------------

/**
 * The backend has no impersonation yet: boot always reports
 * `is_impersonating: false`, so the banner that calls this never shows and
 * there is no `end_impersonation` method to call. Refreshing boot is the whole
 * job until support impersonation exists server-side.
 */
export async function endImpersonation(): Promise<void> {
  await fetchBoot()
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

export async function requestPasswordReset(email: string): Promise<unknown> {
  return rpc('glamora.api.auth.request_password_reset', { email })
}

export async function validateResetKey(key: string): Promise<unknown> {
  return rpcGet('glamora.api.auth.validate_reset_key', { key })
}

export async function resetPasswordWithKey(
  key: string,
  newPassword: string,
): Promise<LoginResponse> {
  const data = requireData(
    (await rpc('glamora.api.auth.reset_password_with_key', {
      key,
      new_password: newPassword,
    })) as LoginResponse | null,
    'the password change',
  )
  // Backend returns full token payload (same shape as login)
  if (data.access_token) {
    tokenStorage.accessToken = data.access_token
    tokenStorage.refreshToken = data.refresh_token
    applyBoot(data.boot ?? data)
  }
  return data
}

// ---------------------------------------------------------------------------
// OTP
// ---------------------------------------------------------------------------

export async function requestOtp(
  email: string,
  purpose: string = 'signup',
): Promise<OtpTiming> {
  return verified<OtpTiming>(
    rpc('glamora.api.auth.request_otp', {
      email,
      purpose,
    }),
    'the new code',
    'resend_after',
  )
}

export async function resetPasswordWithOtp(
  email: string,
  otp: string,
  newPassword: string,
): Promise<LoginResponse> {
  const data = requireData(
    (await rpc('glamora.api.auth.reset_password_with_otp', {
      email,
      otp,
      new_password: newPassword,
    })) as LoginResponse | null,
    'the password change',
  )
  if (data.access_token) {
    tokenStorage.accessToken = data.access_token
    tokenStorage.refreshToken = data.refresh_token
    applyBoot(data.boot ?? data)
  }
  return data
}

export async function verifyOtp(
  email: string,
  otp: string,
): Promise<VerifyOtpResponse> {
  const data = requireData(
    (await rpc('glamora.api.auth.verify_otp', {
      email,
      otp,
    })) as VerifyOtpResponse | null,
    'the code check',
  )
  if (data.access_token) {
    tokenStorage.accessToken = data.access_token
    tokenStorage.refreshToken = data.refresh_token ?? null
    applyBoot(data.boot ?? data)
  }
  return data
}

// ---------------------------------------------------------------------------
// Signup
// ---------------------------------------------------------------------------

export async function signupSalon(
  payload: SignupPayload,
): Promise<SignupResponse> {
  return verified<SignupResponse>(
    rpc('glamora.api.auth.signup_salon', {
      ...payload,
    }),
    'the sign-up',
    'user',
  )
}

export async function verifyEmail(
  user: string,
  token: string,
): Promise<unknown> {
  const res = requireData(
    (await rpc('glamora.api.auth.verify_email', { user, token })) as {
      verified?: number
    } | null,
    'the email address',
  )
  if (res.verified !== 1) {
    throw new Error('The server did not confirm the email address.')
  }
  return res
}

/**
 * Change the current user's password (`glamora.api.auth.change_password`).
 * The backend enforces strength; surface its message on failure.
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<ChangePasswordResponse> {
  const res = requireData(
    (await rpc('glamora.api.auth.change_password', {
      old_password: oldPassword,
      new_password: newPassword,
    })) as ChangePasswordResponse | null,
    'the password change',
  )
  if (!res.changed) {
    throw new Error('The server did not confirm the password change.')
  }
  return res
}

// ---------------------------------------------------------------------------
// Global window augmentation
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    csrf_token?: string
    site_name?: string
    __glamora_socket?: unknown
  }
}

// Needed so this file is treated as a module
export {}
